import { useEffect, useState } from 'react';
import { http } from '../api/http';

// Phase-1 lookup wiring against Aivrmanager.Api. The legacy portal had nine
// dedicated id->name endpoints (variablelist / voicelist / agentlist / ...);
// all of them now have a 1:1 counterpart under
// /api/flowbuilder/lookup/<kind>list, hosted by FlowBuilderController.
//
// Each call is cached by (kind, applicationId) for the lifetime of the SPA
// process. That mirrors the legacy portal's behaviour and keeps the picker
// snappy when the user opens & closes the same property repeatedly.

export interface LookupItem {
  id: number | string;
  name: string;
  category?: string | null;
  voiceCategory?: string | null;
  // Database lookup rows carry their typed column metadata so a future
  // DBLookup sub-form can render a typed input per column. Other lookup
  // kinds leave this undefined.
  columns?: Array<{ name: string; typeId?: number | null }>;
  // Recognizer-only extras: a short description (hint/tooltip) and the
  // scope marker ("global" / "customer") so the UI can show a small badge
  // when a customer override is in effect.
  hint?: string | null;
  scope?: 'global' | 'customer' | null;
  // Voice-preset-only extras. `primaryLanguage` lets the picker render a
  // language-aware optgroup when the list isn't filtered. The remaining
  // fields are only set on the language-filtered variant.
  primaryLanguage?: string | null;
  providerVoiceId?: string | null;
  isPrimaryForLanguage?: boolean | null;
  qualityScore?: number | null;
}

export type LookupKind =
  | 'variable'
  | 'audiofile'
  | 'weekplan'
  | 'agent'
  | 'huntgroup'
  | 'flow'
  | 'application'
  | 'calendar'
  | 'database'
  // Voicebot-era lookups (Mod_VB_* modules + aiServerAgent admin).
  | 'recognizer'
  | 'language'
  | 'voicepreset';

const cache = new Map<string, Promise<LookupItem[]>>();

interface RawLookupItem {
  id?: number | string;
  Id?: number | string;
  name?: string;
  Name?: string;
  category?: string | null;
  Category?: string | null;
  // Database lookup rows
  columns?: Array<{ name?: string; Name?: string; typeId?: number | null; TypeId?: number | null }>;
  Columns?: Array<{ name?: string; Name?: string; typeId?: number | null; TypeId?: number | null }>;
  // Recognizer rows
  hint?: string | null;
  Hint?: string | null;
  scope?: 'global' | 'customer' | null;
  Scope?: 'global' | 'customer' | null;
  // Voice-preset rows
  primaryLanguage?: string | null;
  PrimaryLanguage?: string | null;
  providerVoiceId?: string | null;
  ProviderVoiceId?: string | null;
  isPrimaryForLanguage?: boolean | null;
  IsPrimaryForLanguage?: boolean | null;
  qualityScore?: number | null;
  QualityScore?: number | null;
}

function normalize(rows: RawLookupItem[]): LookupItem[] {
  return rows.map((r) => {
    const cols = (r.columns ?? r.Columns) ?? undefined;
    return {
      id: r.id ?? r.Id ?? '',
      name: r.name ?? r.Name ?? '',
      category: r.category ?? r.Category ?? null,
      columns: cols
        ? cols.map((c) => ({
            name: c.name ?? c.Name ?? '',
            typeId: c.typeId ?? c.TypeId ?? null,
          }))
        : undefined,
      hint: r.hint ?? r.Hint ?? null,
      scope: (r.scope ?? r.Scope ?? null) as LookupItem['scope'],
      primaryLanguage: r.primaryLanguage ?? r.PrimaryLanguage ?? null,
      providerVoiceId: r.providerVoiceId ?? r.ProviderVoiceId ?? null,
      isPrimaryForLanguage: r.isPrimaryForLanguage ?? r.IsPrimaryForLanguage ?? null,
      qualityScore: r.qualityScore ?? r.QualityScore ?? null,
    };
  });
}

// All of these GET against /api/flowbuilder/lookup/<kind>list. We send the
// applicationId only where the legacy SP actually used it (voice/variable/
// flow); the others ignore it server-side.
async function fetchVoices(applicationId: number): Promise<LookupItem[]> {
  const rows = await http<RawLookupItem[]>(
    `/api/flowbuilder/lookup/voicelist?applicationId=${encodeURIComponent(String(applicationId))}`,
  );
  return normalize(rows ?? []);
}

async function fetchHuntgroups(): Promise<LookupItem[]> {
  const rows = await http<RawLookupItem[]>('/api/flowbuilder/lookup/huntgrouplist');
  return normalize(rows ?? []);
}

async function fetchVariables(applicationId: number): Promise<LookupItem[]> {
  const rows = await http<RawLookupItem[]>(
    `/api/flowbuilder/lookup/variablelist?applicationId=${encodeURIComponent(String(applicationId))}`,
  );
  return normalize(rows ?? []);
}

async function fetchAgents(): Promise<LookupItem[]> {
  const rows = await http<RawLookupItem[]>('/api/flowbuilder/lookup/agentlist');
  return normalize(rows ?? []);
}

async function fetchWeekplans(): Promise<LookupItem[]> {
  const rows = await http<RawLookupItem[]>('/api/flowbuilder/lookup/weekplanlist');
  return normalize(rows ?? []);
}

async function fetchCalendars(): Promise<LookupItem[]> {
  const rows = await http<RawLookupItem[]>('/api/flowbuilder/lookup/calendarlist');
  return normalize(rows ?? []);
}

async function fetchFlows(applicationId: number): Promise<LookupItem[]> {
  const rows = await http<RawLookupItem[]>(
    `/api/flowbuilder/lookup/flowlist?applicationId=${encodeURIComponent(String(applicationId))}`,
  );
  return normalize(rows ?? []);
}

async function fetchApplications(): Promise<LookupItem[]> {
  const rows = await http<RawLookupItem[]>('/api/flowbuilder/lookup/applicationlist');
  return normalize(rows ?? []);
}

async function fetchDatabases(): Promise<LookupItem[]> {
  const rows = await http<RawLookupItem[]>('/api/flowbuilder/lookup/databaselist');
  return normalize(rows ?? []);
}

async function fetchRecognizers(): Promise<LookupItem[]> {
  const rows = await http<RawLookupItem[]>('/api/flowbuilder/lookup/recognizerlist');
  return normalize(rows ?? []);
}

async function fetchLanguages(): Promise<LookupItem[]> {
  const rows = await http<RawLookupItem[]>('/api/flowbuilder/lookup/languagelist');
  return normalize(rows ?? []);
}

async function fetchVoicePresets(languageCode: string | null | undefined): Promise<LookupItem[]> {
  const qs = languageCode ? `?language=${encodeURIComponent(languageCode)}` : '';
  const rows = await http<RawLookupItem[]>(`/api/flowbuilder/lookup/voicepresetlist${qs}`);
  return normalize(rows ?? []);
}

// Slots are per-recognizer, so they don't fit the (kind, applicationId) shape
// of `loadLookup`; dedicated fetcher + cache keyed by recognizerKey. Used by the SlotMappingEditor on Mod_VB_Ask nodes.
const slotsCache = new Map<string, Promise<LookupItem[]>>();

export function loadRecognizerSlots(recognizerKey: string | null | undefined): Promise<LookupItem[]> {
  const key = (recognizerKey ?? '').trim();
  if (!key) return Promise.resolve([]);
  let p = slotsCache.get(key);
  if (!p) {
    p = http<RawLookupItem[]>(
      `/api/flowbuilder/lookup/recognizerslots?recognizerKey=${encodeURIComponent(key)}`,
    ).then((rows) => normalize(rows ?? []));
    slotsCache.set(key, p);
  }
  return p;
}

/**
 * React hook variant of `loadRecognizerSlots`:
 * re-fetches when the recognizer key changes or the lookup cache is
 * invalidated. Returns the recognizer's declared slots (id = SlotKey,
 * name = DisplayLabel).
 */
export function useRecognizerSlots(recognizerKey: string | null | undefined) {
  const [items, setItems] = useState<LookupItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [version, setVersion] = useState(0);
  const key = (recognizerKey ?? '').trim();

  useEffect(() => {
    return subscribeLookupChanges(() => {
      slotsCache.clear();
      setVersion((v) => v + 1);
    });
  }, []);

  useEffect(() => {
    if (!key) {
      setItems([]);
      setLoading(false);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    loadRecognizerSlots(key)
      .then((data) => {
        if (!cancelled) setItems(data);
      })
      .catch((ex: Error) => {
        if (!cancelled) setError(ex.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [key, version]);

  return { items, loading, error };
}

/**
 * Optional context for kinds whose fetch depends on more than just the
 * application — currently only `voicepreset` (filtered by language).
 * The cache key includes the relevant context fields so the in-memory
 * cache stays correct when callers ask for different filters.
 */
export interface LookupContext {
  language?: string | null;
}

export function loadLookup(
  kind: LookupKind,
  applicationId: number,
  context: LookupContext = {},
): Promise<LookupItem[]> {
  // Cache key includes applicationId AND any context that affects the
  // result set (language for voice presets) so different filters don't
  // collide on the same cache entry.
  const lang = (context.language ?? '').trim();
  const key = lang ? `${kind}:${applicationId}:lang=${lang}` : `${kind}:${applicationId}`;
  let p = cache.get(key);
  if (!p) {
    switch (kind) {
      case 'audiofile':
        p = fetchVoices(applicationId);
        break;
      case 'huntgroup':
        p = fetchHuntgroups();
        break;
      case 'variable':
        p = fetchVariables(applicationId);
        break;
      case 'agent':
        p = fetchAgents();
        break;
      case 'weekplan':
        p = fetchWeekplans();
        break;
      case 'calendar':
        p = fetchCalendars();
        break;
      case 'flow':
        p = fetchFlows(applicationId);
        break;
      case 'application':
        p = fetchApplications();
        break;
      case 'database':
        p = fetchDatabases();
        break;
      case 'recognizer':
        p = fetchRecognizers();
        break;
      case 'language':
        p = fetchLanguages();
        break;
      case 'voicepreset':
        p = fetchVoicePresets(lang || null);
        break;
      default:
        p = Promise.resolve([]);
        break;
    }
    cache.set(key, p);
  }
  return p;
}

// Cache-invalidation hooks. The properties panel in the Flow Builder shows
// drop-downs (voices, weekplans, variables, …) that originate in other
// pages of the app — when the user adds a voice in another browser tab the
// editor needs to pick it up without a full page refresh. We achieve that
// by:
//   - dropping the affected entries from the in-memory cache, and
//   - notifying every active `useLookup()` hook so it re-fetches.
// The `LookupProvider` additionally wires this up to window focus /
// visibility changes so the lookups refresh on tab switch.

type Listener = (kind: LookupKind | null) => void;
const listeners = new Set<Listener>();

export function invalidateLookup(kind?: LookupKind, applicationId?: number): void {
  if (!kind) {
    // Clear the entire cache (used by the focus/visibility refresh).
    cache.clear();
    slotsCache.clear();
  } else if (applicationId == null) {
    // Drop every key for this kind across all applications + every
    // language-filtered variant for that kind.
    for (const k of Array.from(cache.keys())) {
      if (k.startsWith(`${kind}:`)) cache.delete(k);
    }
  } else {
    // Drop the exact (kind, applicationId) entry plus every
    // language-filtered variant — they all become stale together.
    const exactPrefix = `${kind}:${applicationId}`;
    for (const k of Array.from(cache.keys())) {
      if (k === exactPrefix || k.startsWith(`${exactPrefix}:`)) cache.delete(k);
    }
  }
  for (const cb of listeners) cb(kind ?? null);
}

export function subscribeLookupChanges(cb: Listener): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

/**
 * React hook that loads a lookup list and automatically re-fetches when
 * the cache is invalidated (e.g. after the user creates a new variable
 * or switches back from another tab where they added a voice file).
 *
 * Consumers don't need to manage their own `useEffect` + cancellation —
 * the hook handles cancellation, re-loads on dependency change, and
 * re-loads on cache invalidation for the matching kind.
 */
export function useLookup(
  kind: LookupKind,
  applicationId: number,
  context: LookupContext = {},
) {
  const [items, setItems] = useState<LookupItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [version, setVersion] = useState(0);

  // Stringify the context once so the effect's dependency stays stable
  // when callers pass a fresh object literal on each render.
  const ctxKey = (context.language ?? '').trim();

  useEffect(() => {
    // `kind === null` means "all caches were invalidated" (e.g. focus).
    return subscribeLookupChanges((k) => {
      if (k === null || k === kind) setVersion((v) => v + 1);
    });
  }, [kind]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    loadLookup(kind, applicationId, { language: ctxKey || null })
      .then((data) => {
        if (!cancelled) setItems(data);
      })
      .catch((ex: Error) => {
        if (!cancelled) setError(ex.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [kind, applicationId, ctxKey, version]);

  return { items, loading, error };
}

/// Used by the canvas/properties code to map a parameter's type-name (as it
/// comes back from the module catalog) to the lookup we need to display its
/// value. Lower-cased once and pinned to one consistent kind for each alias.
export function lookupKindForType(typeName: string | null | undefined): LookupKind | null {
  switch ((typeName || '').toLowerCase()) {
    case 'variable':
      return 'variable';
    case 'audiofile':
    case 'voice':
    case 'voicefile':
      return 'audiofile';
    case 'weekplan':
      return 'weekplan';
    case 'agent':
      return 'agent';
    case 'huntgroup':
      return 'huntgroup';
    case 'flow':
      return 'flow';
    case 'application':
      return 'application';
    case 'calendar':
      return 'calendar';
    case 'database':
    case 'simpledb':
      return 'database';
    default:
      return null;
  }
}
